import React, { useMemo } from 'react';
import { Box, Typography, Button, Stack } from 'omaha-design-system';
import type { FilterPool, NbaRatesPlayer } from './types';
import { CONDITIONS, COMPARISONS, getLastName, DEFAULT_AVATAR } from './types';

interface NbaRatesFilterBuilderProps {
  filterPools: FilterPool[];
  players: NbaRatesPlayer[];
  dispatch: React.Dispatch<any>;
}

const NbaRatesFilterBuilder: React.FC<NbaRatesFilterBuilderProps> = ({
  filterPools,
  players,
  dispatch,
}) => {
  const sortedPlayers = useMemo(
    () =>
      [...players].sort((a, b) =>
        getLastName(a.player_name)
          .toLowerCase()
          .localeCompare(getLastName(b.player_name).toLowerCase()),
      ),
    [players],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {filterPools.map((pool) => (
        <FilterPoolRow
          key={pool.id}
          pool={pool}
          sortedPlayers={sortedPlayers}
          allPlayerIds={players.map((p) => p.player_id)}
          dispatch={dispatch}
        />
      ))}

      {/* Add Pool Button */}
      <Button
        onClick={() => dispatch({ type: 'ADD_POOL' })}
        variant="outlined"
        sx={{
          borderStyle: 'dashed',
          borderColor: 'rgba(255,255,255,0.12)',
          color: 'text.secondary',
          textTransform: 'none',
          py: 0.625,
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.3)',
            bgcolor: 'rgba(255,255,255,0.02)',
          },
        }}
      >
        + Add Filter Pool
      </Button>

      {filterPools.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 2 }}
        >
          Click "Add Filter Pool" to create lineup conditions
        </Typography>
      )}
    </Box>
  );
};

// ─── Inline: Filter Pool Row ─────────────────────────────────────────────────

interface FilterPoolRowProps {
  pool: FilterPool;
  sortedPlayers: NbaRatesPlayer[];
  allPlayerIds: string[];
  dispatch: React.Dispatch<any>;
}

const GRID_GAP = 10;
const GRID_PAD = 12;
const MAX_COLS = 11;

const FilterPoolRow: React.FC<FilterPoolRowProps> = ({
  pool,
  sortedPlayers,
  allPlayerIds,
  dispatch,
}) => {
  const conditionMeta = CONDITIONS.find((c) => c.value === pool.condition) || CONDITIONS[0];
  const selectedSet = new Set(pool.playerIds);

  const gridColumns = useMemo(() => {
    const count = sortedPlayers.length;
    if (count === 0) return `repeat(${MAX_COLS}, 1fr)`;

    const cols = Math.min(Math.ceil(count / 2), MAX_COLS);
    return `repeat(${cols}, 1fr)`;
  }, [sortedPlayers.length]);

  const cycleCondition = () => {
    const idx = CONDITIONS.findIndex((c) => c.value === pool.condition);
    const next = CONDITIONS[(idx + 1) % CONDITIONS.length].value;
    dispatch({ type: 'SET_CONDITION', poolId: pool.id, condition: next });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: '#0D0F13',
        border: `1px solid ${conditionMeta.color}40`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Left Control Panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.75,
          p: '10px 14px',
          background: `linear-gradient(90deg, ${conditionMeta.color}10 0%, ${conditionMeta.color}05 100%)`,
          borderRight: `1px solid ${conditionMeta.color}25`,
          minWidth: '95px',
        }}
      >
        {/* Condition Cycle Button */}
        <Box
          component="button"
          onClick={cycleCondition}
          title="Click to cycle: On > Off > Started > Bench > Played"
          sx={{
            width: '100%',
            p: '6px 10px',
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: conditionMeta.color,
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            transition: 'all 0.15s ease',
          }}
        >
          {conditionMeta.label}
        </Box>

        {/* Comparison Pills */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: '#1E2330',
            borderRadius: '4px',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {COMPARISONS.map((c) => (
            <Box
              key={c.value}
              component="button"
              onClick={() =>
                dispatch({
                  type: 'SET_COMPARISON',
                  poolId: pool.id,
                  comparison: c.value,
                })
              }
              sx={{
                flex: 1,
                p: '4px 0',
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor:
                  pool.comparison === c.value
                    ? conditionMeta.color
                    : 'transparent',
                border: 'none',
                color: pool.comparison === c.value ? 'white' : '#6B7280',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {c.symbol}
            </Box>
          ))}
        </Box>

        {/* Count Stepper */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', justifyContent: 'center' }}>
          <Box
            component="button"
            onClick={() =>
              dispatch({
                type: 'SET_COUNT',
                poolId: pool.id,
                count: Math.max(1, pool.count - 1),
              })
            }
            disabled={pool.count <= 1}
            sx={{
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 700,
              bgcolor: pool.count <= 1 ? '#1E2330' : `${conditionMeta.color}40`,
              border: 'none',
              borderRadius: '4px',
              color: pool.count <= 1 ? '#4B5563' : conditionMeta.color,
              cursor: pool.count <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            {'\u2212'}
          </Box>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#E8EAED',
              fontVariantNumeric: 'tabular-nums',
              minWidth: '1.5rem',
              textAlign: 'center',
            }}
          >
            {pool.count}
          </Typography>
          <Box
            component="button"
            onClick={() =>
              dispatch({
                type: 'SET_COUNT',
                poolId: pool.id,
                count: Math.min(pool.playerIds.length || 1, pool.count + 1),
              })
            }
            disabled={pool.count >= (pool.playerIds.length || 1)}
            sx={{
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 700,
              bgcolor:
                pool.count >= (pool.playerIds.length || 1)
                  ? '#1E2330'
                  : `${conditionMeta.color}40`,
              border: 'none',
              borderRadius: '4px',
              color:
                pool.count >= (pool.playerIds.length || 1)
                  ? '#4B5563'
                  : conditionMeta.color,
              cursor:
                pool.count >= (pool.playerIds.length || 1)
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            +
          </Box>
        </Stack>

        {/* Selected count */}
        <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', textAlign: 'center' }}>
          of{' '}
          <Box component="span" sx={{ color: conditionMeta.color, fontWeight: 600 }}>
            {pool.playerIds.length}
          </Box>{' '}
          selected
        </Typography>

        {/* Action Buttons */}
        <Stack spacing={0.5} sx={{ width: '100%', mt: 0.5 }}>
          <Stack direction="row" spacing={0.5}>
            <Box
              component="button"
              onClick={() =>
                dispatch({
                  type: 'SELECT_ALL',
                  poolId: pool.id,
                  playerIds: allPlayerIds,
                })
              }
              sx={{
                flex: 1,
                p: '4px 0',
                fontSize: '0.6rem',
                fontWeight: 600,
                bgcolor: '#1E2330',
                border: 'none',
                borderRadius: '3px',
                color: '#8B95A5',
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              All
            </Box>
            <Box
              component="button"
              onClick={() => dispatch({ type: 'CLEAR_POOL', poolId: pool.id })}
              sx={{
                flex: 1,
                p: '4px 0',
                fontSize: '0.6rem',
                fontWeight: 600,
                bgcolor: '#1E2330',
                border: 'none',
                borderRadius: '3px',
                color: '#8B95A5',
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              Clear
            </Box>
          </Stack>
          <Box
            component="button"
            onClick={() => dispatch({ type: 'REMOVE_POOL', poolId: pool.id })}
            sx={{
              p: '4px 0',
              fontSize: '0.6rem',
              fontWeight: 600,
              bgcolor: '#ef444420',
              border: 'none',
              borderRadius: '3px',
              color: '#ef4444',
              cursor: 'pointer',
              textTransform: 'uppercase',
              width: '100%',
            }}
          >
            {'\u2715'} Remove
          </Box>
        </Stack>
      </Box>

      {/* Player Grid */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: `${GRID_GAP}px`,
          p: `${GRID_PAD}px`,
          alignItems: 'start',
          minWidth: 0,
        }}
      >
        {sortedPlayers.map((player) => (
          <PlayerCard
            key={player.player_id}
            player={player}
            isSelected={selectedSet.has(player.player_id)}
            conditionColor={conditionMeta.color}
            onClick={() =>
              dispatch({
                type: 'TOGGLE_PLAYER',
                poolId: pool.id,
                playerId: player.player_id,
              })
            }
          />
        ))}
      </Box>
    </Box>
  );
};

// ─── Inline: Player Card ─────────────────────────────────────────────────────

interface PlayerCardProps {
  player: NbaRatesPlayer;
  isSelected: boolean;
  conditionColor: string;
  onClick: () => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  isSelected,
  conditionColor,
  onClick,
}) => {
  const lastName = getLastName(player.player_name);

  return (
    <Box
      component="button"
      onClick={onClick}
      title={player.player_name}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '92 / 78',
        p: 0,
        background: isSelected
          ? `linear-gradient(180deg, ${conditionColor}30 0%, ${conditionColor}10 100%)`
          : 'linear-gradient(180deg, #1A1D24 0%, #14171C 100%)',
        border: `2px solid ${isSelected ? conditionColor : '#1E2330'}`,
        borderRadius: '6px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
        boxShadow: isSelected ? `0 0 10px ${conditionColor}50` : 'none',
      }}
    >
      {/* Silhouette icon */}
      <Box
        component="img"
        src={DEFAULT_AVATAR}
        alt=""
        sx={{
          position: 'absolute',
          top: '8%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '50%',
          height: '50%',
          objectFit: 'contain',
          opacity: isSelected ? 0.5 : 0.25,
          transition: 'opacity 0.15s ease',
        }}
      />

      {/* Player name — front and center */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '4px 6px',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            lineHeight: 1.3,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {player.player_name}
        </Typography>
      </Box>

      {/* Selection check */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '18px',
            height: '18px',
            bgcolor: conditionColor,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.65rem',
            color: 'white',
            fontWeight: 700,
            boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
          }}
        >
          {'\u2713'}
        </Box>
      )}
    </Box>
  );
};

export default NbaRatesFilterBuilder;
